import React, { useState, useEffect, useRef } from "react";
import {
  FiArrowLeft,
  FiChevronDown,
  FiCheck,
  FiRefreshCw,
  FiCalendar,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiUser,
  FiTrendingUp,
  FiFileText
} from "react-icons/fi";
import api from "../../../connector/api";
import Pagination from "../reuse/Pagination";

// Reusable Custom Dropdown
function CustomSelect({ options, value, onChange, placeholder = "Select option", className = "" }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedOption = options.find((opt) => String(opt.value) === String(value)) || options[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`relative inline-block text-left ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs text-zinc-700 hover:bg-zinc-100/70 focus:outline-none transition-colors cursor-pointer"
      >
        <span className="truncate">{selectedOption?.label || placeholder}</span>
        <FiChevronDown className={`w-3.5 h-3.5 text-zinc-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-xl min-w-xl max-h-60 overflow-y-auto rounded-lg bg-white border border-zinc-200 shadow-lg py-1 z-30 animate-in fade-in zoom-in-95 duration-100 custom-scroll">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between transition-colors cursor-pointer ${
                String(option.value) === String(value)
                  ? "bg-zinc-100 text-zinc-900 font-medium"
                  : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
              }`}
            >
              <span className="truncate">{option.label}</span>
              {String(option.value) === String(value) && <FiCheck className="w-3 h-3 text-zinc-700 shrink-0 ml-1" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AttendanceDetail({ employee, onBack }) {
  const [historyRecords, setHistoryRecords] = useState([]);
  const [summary, setSummary] = useState({
    compliance: 0,
    present: 0,
    absent: 0,
    late: 0,
    total: 0
  });
  const [loading, setLoading] = useState(true);

  // Dropdown Options
  const [termsList, setTermsList] = useState([{ label: "All Terms & Years", value: "all" }]);

  // Filter States
  const [selectedTerm, setSelectedTerm] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("all");

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const monthOptions = [
    { label: "All Months", value: "all" },
    { label: "January", value: "1" },
    { label: "February", value: "2" },
    { label: "March", value: "3" },
    { label: "April", value: "4" },
    { label: "May", value: "5" },
    { label: "June", value: "6" },
    { label: "July", value: "7" },
    { label: "August", value: "8" },
    { label: "September", value: "9" },
    { label: "October", value: "10" },
    { label: "November", value: "11" },
    { label: "December", value: "12" }
  ];

  const pageSizeOptions = [
    { label: "5 / page", value: 5 },
    { label: "10 / page", value: 10 },
    { label: "20 / page", value: 20 },
    { label: "50 / page", value: 50 }
  ];

  // Fetch School Year / Term list for dropdown
  const fetchTermsAndYears = async () => {
    try {
        const response = await api.get("/year-terms/");
        const data = Array.isArray(response.data) ? response.data : response.data.results || [];
        
        const formatted = data.map((t) => {
        // Extract string names safely from serializer fields
        const yearName = t.school_year_name || t.school_year?.name || "Unknown Year";
        const termName = t.term_name || t.term?.name || "Unknown Term";

        return {
            label: `${yearName} (${termName})`,
            value: t.id
        };
        });

        setTermsList([{ label: "All Terms & Years", value: "all" }, ...formatted]);
    } catch (error) {
        console.error("Error fetching school terms:", error);
    }
    };

  // Fetch Attendance History
  const fetchHistory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage);
      params.append("page_size", itemsPerPage);
      if (selectedMonth !== "all") params.append("month", selectedMonth);
      if (selectedTerm !== "all") params.append("term_year", selectedTerm);

      const response = await api.get(`/attendance/history/${employee.id}/?${params.toString()}`);
      const data = response.data;

      setHistoryRecords(data.results || []);
      setTotalRecords(data.total_records || 0);
      if (data.summary) {
        setSummary(data.summary);
      }
    } catch (error) {
      console.error("Error fetching employee attendance history:", error);
      setHistoryRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTermsAndYears();
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [employee.id, currentPage, itemsPerPage, selectedTerm, selectedMonth]);

  const renderStatusBadge = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "present") {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60">
          <FiCheckCircle className="w-3 h-3" /> Present
        </span>
      );
    }
    if (s === "absent") {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-700 border border-red-200/60">
          <FiXCircle className="w-3 h-3" /> Absent
        </span>
      );
    }
    if (s === "late") {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200/60">
          <FiClock className="w-3 h-3" /> Late
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-zinc-100 text-zinc-600 border border-zinc-200">
        <FiAlertCircle className="w-3 h-3" /> {status || "—"}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header Bar */}
      <div className="flex items-center justify-between pb-2 border-b border-zinc-200/80">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="p-1.5 rounded-lg border border-zinc-200/80 bg-white hover:bg-zinc-50 text-zinc-600 transition-colors cursor-pointer"
          >
            <FiArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h2 className="text-sm font-bold text-zinc-900">{employee.name}</h2>
            <p className="text-[10px] text-zinc-400">
              Ref: {employee.position?.id_number || `EMP-${employee.id}`}
            </p>
          </div>
        </div>

        {/* Custom Filters */}
        <div className="flex items-center gap-2">
          <CustomSelect
            options={termsList}
            value={selectedTerm}
            onChange={(val) => {
              setSelectedTerm(val);
              setCurrentPage(1);
            }}
            placeholder="School Term"
          />
          <CustomSelect
            options={monthOptions}
            value={selectedMonth}
            onChange={(val) => {
              setSelectedMonth(val);
              setCurrentPage(1);
            }}
            placeholder="Month"
          />
        </div>
      </div>

      {/* Summary Metrics Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-white p-3 rounded-lg border border-zinc-200/80 shadow-2xs">
          <p className="text-[10px] font-medium text-zinc-400 uppercase tracking-wider">Compliance</p>
          <div className="flex items-center justify-between mt-1">
            <span className="text-sm font-bold text-zinc-900">{summary.compliance}%</span>
            <FiTrendingUp className="w-4 h-4 text-blue-500" />
          </div>
        </div>

        <div className="bg-white p-3 rounded-lg border border-zinc-200/80 shadow-2xs">
          <p className="text-[10px] font-medium text-zinc-400 uppercase tracking-wider">Present</p>
          <div className="flex items-center justify-between mt-1">
            <span className="text-sm font-bold text-emerald-600">{summary.present}</span>
            <FiCheckCircle className="w-4 h-4 text-emerald-500" />
          </div>
        </div>

        <div className="bg-white p-3 rounded-lg border border-zinc-200/80 shadow-2xs">
          <p className="text-[10px] font-medium text-zinc-400 uppercase tracking-wider">Absent</p>
          <div className="flex items-center justify-between mt-1">
            <span className="text-sm font-bold text-red-600">{summary.absent}</span>
            <FiXCircle className="w-4 h-4 text-red-500" />
          </div>
        </div>

        <div className="bg-white p-3 rounded-lg border border-zinc-200/80 shadow-2xs">
          <p className="text-[10px] font-medium text-zinc-400 uppercase tracking-wider">Late</p>
          <div className="flex items-center justify-between mt-1">
            <span className="text-sm font-bold text-amber-600">{summary.late}</span>
            <FiClock className="w-4 h-4 text-amber-500" />
          </div>
        </div>

        <div className="bg-white p-3 rounded-lg border border-zinc-200/80 shadow-2xs col-span-2 sm:col-span-1">
          <p className="text-[10px] font-medium text-zinc-400 uppercase tracking-wider">Total Records</p>
          <div className="flex items-center justify-between mt-1">
            <span className="text-sm font-bold text-zinc-700">{summary.total}</span>
            <FiFileText className="w-4 h-4 text-zinc-400" />
          </div>
        </div>
      </div>

      {/* Attendance History Table */}
      <div className="bg-white rounded-lg border border-zinc-200/80 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-zinc-50/70 border-b border-zinc-200/80 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                <th className="py-3 px-4">Date</th>
                <th className="py-3 px-4">Check-In Time</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Term / Year</th>
                <th className="py-3 px-4">Comment</th>
                <th className="py-3 px-4 text-right">Recorded By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 text-xs text-zinc-700">
              {loading ? (
                <tr>
                  <td colSpan="6" className="py-12 text-center text-zinc-400">
                    <FiRefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-zinc-400" />
                    Loading employee attendance history...
                  </td>
                </tr>
              ) : historyRecords.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-12 text-center text-zinc-400">
                    No attendance records found for the selected filters.
                  </td>
                </tr>
              ) : (
                historyRecords.map((rec) => {
                  const recordedBy = rec.recorded_by;
                  const recordedByName = recordedBy
                    ? `${recordedBy.first_name || ""} ${recordedBy.last_name || recordedBy.username}`.trim()
                    : "System";

                  return (
                    <tr key={rec.id} className="hover:bg-zinc-50 transition-colors">
                      <td className="py-3 px-4 font-mono text-[11px] text-zinc-800">
                        <div className="flex items-center gap-1.5">
                          <FiCalendar className="w-3.5 h-3.5 text-zinc-400" />
                          <span>{rec.date}</span>
                        </div>
                      </td>

                      <td className="py-3 px-4 font-mono text-[11px] text-zinc-700">
                        {rec.check_in ? (
                          <div className="flex items-center gap-1.5">
                            <FiClock className="w-3.5 h-3.5 text-zinc-400" />
                            <span>{rec.check_in}</span>
                          </div>
                        ) : (
                          <span className="text-zinc-400">—</span>
                        )}
                      </td>

                      <td className="py-3 px-4">{renderStatusBadge(rec.status)}</td>

                      <td className="py-3 px-4 text-zinc-500 text-[11px]">
                        {rec.year_term || "—"}
                      </td>

                      <td className="py-3 px-4 text-zinc-500 max-w-[220px] truncate">
                        {rec.comment || "—"}
                      </td>

                      <td className="py-3 px-4 text-right text-zinc-600">
                        <div className="flex items-center justify-end gap-1 text-[11px]">
                          <FiUser className="w-3 h-3 text-zinc-400" />
                          <span>{recordedByName}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer Pagination */}
        <Pagination
          currentPage={currentPage}
          totalCount={totalRecords}
          pageSize={itemsPerPage}
          onPageChange={setCurrentPage}
          onPageSizeChange={(newSize) => {
            setItemsPerPage(newSize);
            setCurrentPage(1);
          }}
          pageSizeOptions={pageSizeOptions}
        />
      </div>
    </div>
  );
}